import {PlatformIdentityService} from './services/auth/identity/platform-identity.service';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {ClientService} from './services/auth/client.service';
import {ClientLoadingService} from './services/auth/client-loading.service';
import {combineLatest, filter, map, Observable, startWith, Subject, Subscription, takeUntil, timer} from 'rxjs';
import {CommonModule} from '@angular/common';
import {ShineAuthService} from './services/auth/shine-auth/shine-auth.service';
import {
    ClientWithProducts,
    getMsalAccountFingerprint,
    ShineUserInfoService
} from './services/auth/shine-auth/shine-user-info.service';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus} from '@azure/msal-browser';
import {ClientSelectionDialogService} from './services/layout/client-selection-dialog.service';
import {ClientStateService} from './services/auth/client-state.service';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRadioModule} from '@angular/material/radio';
import {FormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatChipsModule} from '@angular/material/chips';
import {isEnrichVisualFixtureUrl} from './components/enrich/visual-fixture/enrich-visual-fixture.guard';
import {isWebinarsVisualFixtureUrl} from './components/webinars/visual-fixture/webinars-visual-fixture.guard';
import {isConversationVisualFixtureUrl} from './components/conversations/visual-fixture/conversation-visual-fixture.guard';
import {isClientSettingsVisualFixtureUrl} from './components/client-settings/visual-fixture/client-settings-visual-fixture.guard';
import {
  isProductCardAnalyticsVisualFixtureUrl
} from './components/dashboard/product-card-analytics/product-card-analytics-visual-fixture.guard';
import {
  isFrontendSettingsVisualFixtureUrl
} from './components/chatbots/settings/frontend-settings/frontend-settings-visual-fixture.guard';
import {
  isChatbotSettingsVisualFixtureUrl
} from './components/chatbots/settings/chatbot-settings-visual-fixture.guard';
import {
  isFunctionGeneralSettingsVisualFixtureUrl
} from './components/functions/function-settings/function-general-settings/function-general-settings-visual-fixture.guard';
import {
  isFunctionWorkflowBindingSelectorVisualFixtureUrl
} from './components/functions/function-settings/function-workflow-binding-selector/function-workflow-binding-selector-visual-fixture.guard';
import {isChatbotVisualFixtureUrl} from './components/chatbots/visual-fixture/chatbot-visual-fixture.guard';
import {isWidgetPreviewFixtureUrl} from './components/chatbots/widget-preview-fixture/widget-preview-fixture.guard';
import {isProductFeedVisualFixtureUrl} from './components/product-feeds/product-feeds-settings/product-feed-settings/product-feed-settings.visual-fixture';
import {isListingsVisualFixtureUrl} from './components/objects/listings/visual-fixture/listings-visual-fixture.guard';
import {
  isInterfaceLoadVisualFixtureUrl,
  readInterfaceLoadVisualFixture
} from './components/testing/interface-load-repro/interface-load-repro.guard';
import {isUserManagementVisualFixtureUrl} from './components/settings/users/user-management-visual-fixture.guard';
import {
  isTranslationsVisualFixtureUrl
} from './components/translations/visual-fixture/translations-visual-fixture.guard';
import {ShellLoadFailureKind} from './services/auth/shell-load-failure.util';
import {ClientLoadingReason, ClientLoadingState} from './services/auth/client-state.service';
import {isPublicProductPageUrl} from './components/public-product-pages/public-product-content';
import {isConnectWorkflowVisualFixtureUrl} from './components/connect/workflows/connect-workflow-visual-fixture';
import {
  isConnectWorkflowCanvasVisualFixtureUrl
} from './components/connect/workflows/connect-workflow-canvas-visual-fixture.guard';
import {loginRequest} from './auth.config';
import {getSafeBackofficeReturnTarget, hasOidcCallbackMaterial} from './services/auth/oidc-return-route.util';

interface ShellLoadingOverlay {
    message: string;
    reason: ClientLoadingReason;
}

@Component({
  selector: 'app-root',
  standalone: true,
    imports: [
        RouterOutlet,
        CommonModule,
        MatProgressSpinnerModule,
        MatRadioModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatChipsModule
    ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    private shineAuthService = inject(ShineAuthService);
    private clientService = inject(ClientService);
    private clientLoadingService = inject(ClientLoadingService);
    private clientStateService = inject(ClientStateService);
    private router = inject(Router);
    private msalService = inject(MsalService, { optional: true })!;
    private identity = inject(PlatformIdentityService);
    private msalBroadcastService = inject(MsalBroadcastService, { optional: true })!;
    private shineUserInfoService = inject(ShineUserInfoService);
    private clientSelectionDialog = inject(ClientSelectionDialogService);

  loginDisplay = false;
  isIframe = false;
  private readonly _destroy$ = new Subject<void>();

  // Flag to prevent multiple login attempts
  private isLoginInProgress = false;
  private authoritativeAccountFingerprint: string | null = null;
  private pendingAccountFingerprint: string | null = null;
  private pendingAccountGeneration: number | null = null;
  private failedAccountFingerprint: string | null = null;
  private accountGeneration = 0;
  private destroyed = false;
  private bootstrapRetry: Subscription | null = null;
  private readonly initialBrowserUrl = this.getCurrentUrl();
  // Long enough for normal Entra processing, while recovering before the hosted 22.5-second stall.
  private readonly oidcCallbackTimeoutMs = 12_000;

    // Client loading overlay state from ClientStateService
    loadingOverlay$: Observable<{message: string; reason: ClientLoadingReason} | null>;
    shellLoadFailure$ = this.clientStateService.shellLoadFailure$;

    // No longer need account selection properties as we're using the ClientSelectionDialogService

    constructor() {
      this.loadingOverlay$ = combineLatest([
        this.clientStateService.clientLoadingState$,
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            startWith(null)
        ),
      ]).pipe(
          map(([state]) => this.buildLoadingOverlay(state))
      );

      this.router.events
          .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this._destroy$))
          .subscribe((event: any) => {
              const clientId = this.clientService.client?.id;
              if (clientId) {
                  localStorage.setItem(`shine_last_page_${clientId}`, event.urlAfterRedirects);
              }
          });
  }

  ngOnInit(): void {
    if (hasOidcCallbackMaterial(this.initialBrowserUrl)) {
      this.clientStateService.setOidcCallbackDisposition('pending');
    }

    if (this.applyPublicProductPageState()) {
      return;
    }

    if (!this.identity.auth0 && this.applyLocalInterfaceLoadFixtureState()) {
      return;
    }

    if (this.identity.auth0) {
      if (this.initialBrowserUrl.startsWith('/authentication/login')) {
        this.clientStateService.setClientLoading(false, 'auth-session-loading');
        return;
      }
      const auth0 = this.identity.auth0;
      auth0.ensureSession().then(() => {
        if (this.destroyed) return;
        // The initial null emission means restoration is pending, not logout.
        auth0.session$.pipe(takeUntil(this._destroy$)).subscribe(session => {
          this.loginDisplay = Boolean(session);
          if (session) this.completeAuthoritativeIdentityBootstrap(this.identity.fingerprint, false);
          else {
            this.cancelBootstrapRetry();
            this.completeAuthoritativeIdentityBootstrap(null, false);
            this.clientStateService.setClientLoading(false, 'auth-session-loading');
          }
        });
      }).catch(() => {
        if (this.destroyed) return;
        this.completeAuthoritativeIdentityBootstrap(null, false);
        this.clientStateService.setClientLoading(false, 'auth-session-loading');
        this.clientStateService.setShellLoadFailure('auth-required');
      });
      return;
    }

    this.msalService.initialize().subscribe(() => {
      this.subscribeToMsalEvents();
      this.updateLoginDisplay();

      // Handle redirect before evaluating cached accounts. The redirect result is authoritative.
      if (hasOidcCallbackMaterial(this.initialBrowserUrl)) {
        this.handleOidcCallback();
        return;
      }

      this.continueCachedAccountBootstrap();
    });
  }

  private continueCachedAccountBootstrap(): void {
      const lastLoginStr = localStorage.getItem('shine_last_login');
      const activeAccount = this.ensureActiveAccount();
      if (lastLoginStr && activeAccount) {
        const lastLogin = parseInt(lastLoginStr, 10);
        if (!isNaN(lastLogin) && Date.now() - lastLogin < 24 * 60 * 60 * 1000) {
          this.completeAuthoritativeAccountBootstrap(activeAccount, false);
          return;
        }
      }
      this.markAccountSelectionRequiredWhenAmbiguous();
  }

  private replaceOidcCallbackHistory(): void {
      if (typeof window === 'undefined' || !window.history) {
          return;
      }

      const safeTarget = getSafeBackofficeReturnTarget(this.initialBrowserUrl) || '/ai/dashboard';
      const currentUrl = this.getCurrentUrl();
      if (currentUrl !== safeTarget) {
          window.history.replaceState(window.history.state, '', safeTarget);
      }
  }

  private handleOidcCallback(): void {
      let callbackSettled = false;
      const timeout = window.setTimeout(() => {
          if (callbackSettled) {
              return;
          }

          callbackSettled = true;
          this.clientStateService.setOidcCallbackDisposition('timed-out');
          this.handleOidcCallbackFailure();
      }, this.oidcCallbackTimeoutMs);

      this.msalService.instance.handleRedirectPromise()
          .then((result: AuthenticationResult | null) => {
              if (callbackSettled) {
                  return;
              }

              callbackSettled = true;
              window.clearTimeout(timeout);
              if (result?.accessToken && result.account) {
                  this.clientStateService.setOidcCallbackDisposition(null);
                  this.replaceOidcCallbackHistory();
                  this.completeAuthoritativeAccountBootstrap(result.account);
              } else {
                  this.clientStateService.setOidcCallbackDisposition('failed');
                  this.handleOidcCallbackFailure();
              }
          })
          .catch(() => {
              if (callbackSettled) {
                  return;
              }

              callbackSettled = true;
              window.clearTimeout(timeout);
              this.clientStateService.setOidcCallbackDisposition('failed');
              this.handleOidcCallbackFailure();
          });
  }

  private handleOidcCallbackFailure(): void {
      this.replaceOidcCallbackHistory();
      this.clientStateService.setClientLoading(false, 'backend-unavailable');
  }

  private subscribeToMsalEvents(): void {
    this.msalBroadcastService.inProgress$
        .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            takeUntil(this._destroy$)
        )
        .subscribe(() => {
          if (this.clientStateService.getOidcCallbackDisposition() !== null) {
            return;
          }

          this.updateLoginDisplay();
          this.synchronizeEffectiveAccount(this.ensureActiveAccount());
          this.markAccountSelectionRequiredWhenAmbiguous();
        });

    this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
            takeUntil(this._destroy$)
        )
        .subscribe((msg: EventMessage) => {
          if (this.clientStateService.getOidcCallbackDisposition() !== null) {
            return;
          }

          this.completeAuthoritativeAccountBootstrap(msg.payload as AccountInfo);
        });

    this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED),
            takeUntil(this._destroy$)
        )
        .subscribe(() => {
          if (this.clientStateService.getOidcCallbackDisposition() !== null) {
            return;
          }

          this.updateLoginDisplay();
          const activeAccount = this.msalService.instance.getActiveAccount();
          this.synchronizeEffectiveAccount(activeAccount);
          if (!activeAccount) {
            this.synchronizeEffectiveAccount(this.ensureActiveAccount());
          }
          this.markAccountSelectionRequiredWhenAmbiguous();
        });

    this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
            takeUntil(this._destroy$)
        )
        .subscribe(() => {
          this.authoritativeAccountFingerprint = null;
          this.pendingAccountFingerprint = null;
          this.pendingAccountGeneration = null;
          this.failedAccountFingerprint = null;
          this.accountGeneration++;
          this.resetIdentityDerivedState();
          this.msalService.instance.setActiveAccount(null);
          this.updateLoginDisplay();
          if (this.clientStateService.getOidcCallbackDisposition() !== null) {
              return;
          }

          const remainingAccount = this.ensureActiveAccount();
          if (remainingAccount) {
              this.completeAuthoritativeAccountBootstrap(remainingAccount, false);
          }
          this.markAccountSelectionRequiredWhenAmbiguous();
        });
  }

    // No longer need account selection UI methods as we're using the ClientSelectionDialogService

  /**
   * Gets user and client information from Shine Auth using the new user/rights API
   */
  private getUserInfoFromShineAuth(
      forceRefresh = false,
      expectedAccountFingerprint: string | null = null,
      expectedAccountGeneration: number | null = null,
      preferredClientId?: string,
  ): void {
      this.cancelBootstrapRetry();
      // Ensure loading state is active
      this.clientStateService.setClientLoading(true, 'auth-session-loading');
      this.clientStateService.setBetaBackendUnavailable(false);
      this.clientStateService.clearNonTokenShellLoadFailure();

    this.shineUserInfoService.getUserRights(forceRefresh)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
        next: async (response) => {
            if (!this.isExpectedAccountActive(expectedAccountFingerprint, expectedAccountGeneration)) {
                return;
            }

            if (response?.status === 'success' && response.data && response.data.clients) {
                this.clientStateService.clearTokenAcquisitionFailure();
                const clients = response.data.clients;

                if (clients.length > 0) {
                    const savedClientId = preferredClientId ?? this.getSavedClientId();
                    await this.processSelectedAccount(
                        savedClientId ?? '',
                        clients,
                        expectedAccountFingerprint,
                        expectedAccountGeneration,
                    );
                } else {
                    console.warn('User has no clients');
                    this.clientStateService.setClientLoading(false, 'auth-session-loading');
                    this.clientStateService.setShellLoadFailure('no-client-access');
                    this.scheduleBootstrapRetry(expectedAccountFingerprint, expectedAccountGeneration, preferredClientId);
                }

                if (this.isExpectedAccountActive(expectedAccountFingerprint, expectedAccountGeneration)) {
                    this.markAccountBootstrapComplete(expectedAccountFingerprint, expectedAccountGeneration);
                }
            } else {
                this.releasePendingAccountBootstrap(expectedAccountFingerprint, expectedAccountGeneration);
                console.warn('Failed to get user rights or no clients found');

                // Fallback to the old behavior if the response is invalid
                console.log('Refreshing clients');
                this.clientLoadingService.refreshClients();

                // Navigate based on active product
                this.navigateToActiveProduct();
            }
      },
      error: error => {
        if (!this.isExpectedAccountActive(expectedAccountFingerprint, expectedAccountGeneration)) {
            return;
        }

        this.releasePendingAccountBootstrap(expectedAccountFingerprint, expectedAccountGeneration);
        this.clientLoadingService.markShellLoadFailure(error);
        console.error('Error getting user rights from Shine Auth');
        if (error?.name === 'TimeoutError' || [0, 408, 429].includes(error?.status) || error?.status >= 500) {
            this.scheduleBootstrapRetry(expectedAccountFingerprint, expectedAccountGeneration, preferredClientId);
        }
      }
    });
  }

    private cancelBootstrapRetry(): void {
        this.bootstrapRetry?.unsubscribe();
        this.bootstrapRetry = null;
    }

    private scheduleBootstrapRetry(
        expectedFingerprint: string | null,
        expectedGeneration: number | null,
        preferredClientId?: string,
    ): void {
        const fingerprint = expectedFingerprint ?? this.identity.fingerprint;
        const generation = expectedGeneration ?? this.accountGeneration;
        if (!fingerprint || !this.isExpectedAccountActive(fingerprint, generation)) return;
        this.cancelBootstrapRetry();
        this.bootstrapRetry = timer(30_000, 30_000).pipe(takeUntil(this._destroy$)).subscribe(() => {
            if (!this.isExpectedAccountActive(fingerprint, generation)) {
                this.cancelBootstrapRetry();
                return;
            }
            if (document.visibilityState === 'hidden') return;
            this.pendingAccountFingerprint = fingerprint;
            this.pendingAccountGeneration = generation;
            this.failedAccountFingerprint = null;
            this.authoritativeAccountFingerprint = null;
            // Starting the request cancels this timer; failures schedule the next attempt.
            this.getUserInfoFromShineAuth(true, fingerprint, generation, preferredClientId);
        });
    }

    private completeAuthoritativeAccountBootstrap(account: AccountInfo | null, recordLogin = true): void {
        const fingerprint = getMsalAccountFingerprint(account);
        if (account && this.identity.fingerprint !== fingerprint) this.msalService.instance.setActiveAccount(account);
        this.completeAuthoritativeIdentityBootstrap(fingerprint, recordLogin);
    }

    private completeAuthoritativeIdentityBootstrap(accountFingerprint: string | null, recordLogin = true): void {
        if (this.destroyed) {
            return;
        }

        if (!accountFingerprint) {
            this.authoritativeAccountFingerprint = null;
            this.pendingAccountFingerprint = null;
            this.pendingAccountGeneration = null;
            this.failedAccountFingerprint = null;
            this.accountGeneration++;
            this.resetIdentityDerivedState();
            return;
        }

        if (
            this.pendingAccountFingerprint === accountFingerprint
            || this.authoritativeAccountFingerprint === accountFingerprint
        ) {
            return;
        }

        const boundAccountFingerprint = this.pendingAccountFingerprint
            ?? this.authoritativeAccountFingerprint
            ?? this.failedAccountFingerprint;
        const preferredClientId = !recordLogin
            && (!boundAccountFingerprint || boundAccountFingerprint === accountFingerprint)
            ? this.getSavedClientId()
            : undefined;
        this.authoritativeAccountFingerprint = null;
        this.pendingAccountFingerprint = accountFingerprint;
        this.pendingAccountGeneration = ++this.accountGeneration;
        this.failedAccountFingerprint = null;

        if (recordLogin) {
            localStorage.setItem('shine_last_login', Date.now().toString());
        }
        this.resetIdentityDerivedState();
        this.getUserInfoFromShineAuth(
            true,
            accountFingerprint,
            this.pendingAccountGeneration,
            preferredClientId,
        );
    }

    private synchronizeEffectiveAccount(account: AccountInfo | null): void {
        const accountFingerprint = getMsalAccountFingerprint(account);
        const boundAccountFingerprint = this.pendingAccountFingerprint
            ?? this.authoritativeAccountFingerprint
            ?? this.failedAccountFingerprint;
        if (!accountFingerprint) {
            if (boundAccountFingerprint) {
                this.authoritativeAccountFingerprint = null;
                this.pendingAccountFingerprint = null;
                this.pendingAccountGeneration = null;
                this.failedAccountFingerprint = null;
                this.accountGeneration++;
                this.resetIdentityDerivedState();
            }
            this.clientStateService.setClientLoading(false, 'auth-session-loading');
            this.clientStateService.clearTokenAcquisitionFailure();
            this.clientStateService.setShellLoadFailure(
                this.msalService.instance.getAllAccounts().length > 1
                    ? 'account-selection-required'
                    : 'auth-required'
            );
            return;
        }

        if (!boundAccountFingerprint || accountFingerprint !== boundAccountFingerprint) {
            this.completeAuthoritativeAccountBootstrap(account, false);
        }
    }

    private isExpectedAccountActive(
        expectedAccountFingerprint: string | null,
        expectedAccountGeneration: number | null,
    ): boolean {
        return !this.destroyed
            && (expectedAccountFingerprint === null
            || (
                expectedAccountFingerprint === this.identity.fingerprint
                && expectedAccountGeneration === this.accountGeneration
            ));
    }

    private markAccountBootstrapComplete(
        accountFingerprint: string | null,
        accountGeneration: number | null,
    ): void {
        if (
            accountFingerprint
            && this.pendingAccountFingerprint === accountFingerprint
            && this.pendingAccountGeneration === accountGeneration
        ) {
            this.pendingAccountFingerprint = null;
            this.pendingAccountGeneration = null;
            this.authoritativeAccountFingerprint = accountFingerprint;
            this.failedAccountFingerprint = null;
        }
    }

    private releasePendingAccountBootstrap(
        accountFingerprint: string | null,
        accountGeneration: number | null,
    ): void {
        if (
            accountFingerprint
            && this.pendingAccountFingerprint === accountFingerprint
            && this.pendingAccountGeneration === accountGeneration
        ) {
            this.pendingAccountFingerprint = null;
            this.pendingAccountGeneration = null;
            this.failedAccountFingerprint = accountFingerprint;
        }
    }

    private resetIdentityDerivedState(): void {
        this.cancelBootstrapRetry();
        this.shineUserInfoService.clearCache();
        this.clientService.resetIdentityDerivedState();
    }

    private getSavedClientId(): string | undefined {
        return document.cookie.split(';')
            .find(cookie => cookie.trim().startsWith('clientId='))
            ?.split('=')[1];
    }

    /**
     * Processes the selected account
     * @param accountId The selected account ID
     */
    private async processSelectedAccount(
        accountId: string,
        authoritativeRights?: ClientWithProducts[],
        expectedAccountFingerprint: string | null = null,
        expectedAccountGeneration: number | null = null,
    ): Promise<void> {
        if (!this.isExpectedAccountActive(expectedAccountFingerprint, expectedAccountGeneration)) {
            return;
        }

        if (authoritativeRights) {
            await this.clientService.applyAuthoritativeRights(authoritativeRights, accountId || undefined);
            if (!this.isExpectedAccountActive(expectedAccountFingerprint, expectedAccountGeneration)) {
                return;
            }
            accountId = this.clientService.client?.id ?? '';
        }

        if (accountId) {
            console.log(`Processing selected account: ${accountId}`);

            // Account selector has been replaced with EntitySelectionDialog

            if (!authoritativeRights) {
                // Set the client ID in the ClientService
                await this.clientService.setClientAsync(accountId);
            }

            if (!this.isExpectedAccountActive(expectedAccountFingerprint, expectedAccountGeneration)) {
                return;
            }

            this.clientStateService.setBetaBackendUnavailable(false);

            if (!authoritativeRights) {
                // Refresh clients after successful authentication when no authoritative
                // bootstrap response was supplied by the caller.
                console.log('Refreshing clients');
                this.clientLoadingService.refreshClients();
            }

            // Preserve an explicitly requested route during auth/client bootstrap.
            this.navigateToActiveProduct(this.getInitialBackofficeRoute());
        } else {
            console.warn('No account selected, cannot proceed');
            // Make sure to set loading to false if no account was selected
            this.clientStateService.setClientLoading(false, 'route-loading');
        }
    }

  /**
   * Navigates to the appropriate page based on the active product
   */
  private navigateToActiveProduct(preferredRoute: string | null = null): void {
    const activeProduct = this.clientService.getActiveProduct();

      // Wait for client loading to complete before navigation
      this.clientLoadingService.ensureClientsLoaded().subscribe({
          next: () => {
              console.log('Clients loaded, navigating to active product');

              const clientId = this.clientService.client?.id;
              const lastPath = clientId ? localStorage.getItem(`shine_last_page_${clientId}`) : null;
              if (preferredRoute) {
                  this.router.navigateByUrl(preferredRoute).then(() => {
                      this.clientStateService.setClientLoading(false, 'route-loading');
                  });
              } else if (lastPath) {
                  this.router.navigateByUrl(lastPath).then(() => {
                      this.clientStateService.setClientLoading(false, 'route-loading');
                  });
              } else if (activeProduct === 'Shine Enrich') {
                  this.router.navigate(['/enrich/settings']).then(() => {
                      // Set loading to false after navigation is complete
                      this.clientStateService.setClientLoading(false, 'route-loading');
                  });
              } else {
                  // Default to dashboard for Shine AI or any other product
                  this.router.navigate(['/ai/dashboard']).then(() => {
                      // Set loading to false after navigation is complete
                      this.clientStateService.setClientLoading(false, 'route-loading');
                  });
              }
          },
          error: (err) => {
              this.clientLoadingService.markShellLoadFailure(err);
              console.error('Error ensuring clients loaded');
          }
      });
  }

  private getInitialBackofficeRoute(): string | null {
      return getSafeBackofficeReturnTarget(this.initialBrowserUrl);
  }

  protected readonly shellLoadFailureContent: Record<ShellLoadFailureKind, {title: string; message: string; actionLabel: string}> = {
      'auth-required': {
          title: 'Your session needs attention',
          message: 'Sign in again to continue. We could not complete this request with the current session.',
          actionLabel: 'Sign in again',
      },
      'access-denied': {
          title: 'You do not have access to this page',
          message: 'Your account is signed in, but this route is not available for the current tenant or role.',
          actionLabel: 'Go to dashboard',
      },
      'load-failed': {
          title: 'We could not finish loading this page',
          message: 'Reload the page or try again in a moment. Nothing was changed.',
          actionLabel: 'Reload page',
      },
      'no-client-access': {
          title: 'This account does not have Shine access',
          message: 'This account has no client access. Ask a Shine administrator to link your account and assign permissions.',
          actionLabel: 'Choose another account',
      },
      'account-selection-required': {
          title: 'Choose the account to use with Shine',
          message: 'More than one Microsoft account is available in this browser.',
          actionLabel: 'Choose another account',
      },
  };

  retryShellLoadFailure(failure: ShellLoadFailureKind): void {
      if (this.identity.auth0 && ['auth-required', 'no-client-access', 'account-selection-required'].includes(failure)) {
          if (failure === 'no-client-access') this.identity.auth0.logout().catch(() => this.clientStateService.setShellLoadFailure('auth-required'));
          else this.identity.auth0.login(this.router.url);
          return;
      }
      if (failure === 'auth-required') {
          this.msalService.instance.loginRedirect(loginRequest).catch(() => undefined);
          return;
      }

      if (failure === 'access-denied') {
          this.router.navigate(['/ai/dashboard']).catch(() => undefined);
          return;
      }

      if (failure === 'no-client-access' || failure === 'account-selection-required') {
          this.authoritativeAccountFingerprint = null;
          this.pendingAccountFingerprint = null;
          this.pendingAccountGeneration = null;
          this.failedAccountFingerprint = null;
          this.accountGeneration++;
          this.resetIdentityDerivedState();
          this.msalService.instance.loginRedirect({...loginRequest, prompt: 'select_account'}).catch(() => undefined);
          return;
      }

      window.location.reload();
  }

  private applyLocalInterfaceLoadFixtureState(): boolean {
      if (typeof window === 'undefined' || !window.location) {
          return false;
      }

      if (isClientSettingsVisualFixtureUrl(window.location.href)) {
        this.clientStateService.setClientLoading(false, 'route-loading');
        this.clientStateService.setBetaBackendUnavailable(false);
        this.clientStateService.setShellLoadFailure(null);
        return true;
      }

      if (isUserManagementVisualFixtureUrl(window.location.href)) {
        this.clientStateService.setClientLoading(false, 'route-loading');
        this.clientStateService.setBetaBackendUnavailable(false);
        this.clientStateService.setShellLoadFailure(null);
        return true;
      }

      if (
        isConnectWorkflowVisualFixtureUrl(window.location.href)
        || isConnectWorkflowCanvasVisualFixtureUrl(window.location.href)
        || isTranslationsVisualFixtureUrl(window.location.href)
      ) {
        this.clientStateService.setClientLoading(false, 'route-loading');
        this.clientStateService.setBetaBackendUnavailable(false);
        this.clientStateService.setShellLoadFailure(null);
        return true;
      }

      const fixture = readInterfaceLoadVisualFixture(window.location.href);
      if (!fixture) {
          return false;
      }

      this.clientStateService.setClientLoading(false, 'route-loading');
      this.clientStateService.setBetaBackendUnavailable(false);

      if (fixture.state === 'beta-unavailable') {
          this.clientStateService.setShellLoadFailure(null);
          return true;
      }

      this.clientStateService.setShellLoadFailure(fixture.state);
      return true;
  }

  private applyPublicProductPageState(): boolean {
      if (typeof window === 'undefined' || !window.location || !isPublicProductPageUrl(window.location.href)) {
          return false;
      }

      this.clientStateService.setClientLoading(false, 'route-loading');
      this.clientStateService.setBetaBackendUnavailable(false);
      this.clientStateService.setShellLoadFailure(null);
      return true;
  }

  private buildLoadingOverlay(state: ClientLoadingState): ShellLoadingOverlay | null {
      if (this.shouldSuppressClientLoadingOverlay()) {
          return null;
      }

      if (!state.isLoading && state.reason !== 'backend-unavailable' && state.reason !== 'rbac-tenant-denial') {
          return null;
      }

      return {
          reason: state.reason,
          message: this.resolveLoadingMessage(state.reason)
      };
  }

  private resolveLoadingMessage(reason: ClientLoadingReason): string {
      switch (reason) {
          case 'auth-session-loading':
              return 'Checking your session...';
          case 'backend-unavailable':
              return 'Shine services are temporarily unavailable.';
          case 'rbac-tenant-denial':
              return 'You do not have access to this area.';
          case 'route-loading':
          default:
              return this.getRouteLoadingMessage(this.getCurrentUrl());
      }
  }

  private getCurrentUrl(): string {
      if (typeof window !== 'undefined' && window.location) {
          return `${window.location.pathname}${window.location.search}${window.location.hash}`;
      }

      return this.router.url;
  }

  private getRouteLoadingMessage(url: string): string {
      const path = url.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';

      if (path.startsWith('/ai/')) {
          return 'Loading Shine AI...';
      }

      if (path.startsWith('/webinars')) {
          return 'Loading webinars...';
      }

      if (path.startsWith('/enrich')) {
          return 'Loading enrichment...';
      }

      if (path.startsWith('/settings')) {
          return 'Loading settings...';
      }

      return 'Loading the backoffice...';
  }

  private updateLoginDisplay(): void {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  private ensureActiveAccount(): AccountInfo | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 1) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      return accounts[0];
    }

    return null;
  }

  private markAccountSelectionRequiredWhenAmbiguous(): void {
    if (!this.msalService.instance.getActiveAccount() && this.msalService.instance.getAllAccounts().length > 1) {
      this.clientStateService.setClientLoading(false, 'auth-session-loading');
      this.clientStateService.setShellLoadFailure('account-selection-required');
    }
  }

  shouldSuppressClientLoadingOverlay(): boolean {
    if (typeof window === 'undefined' || !window.location) {
      return false;
    }

    return isEnrichVisualFixtureUrl(window.location.href)
      || isPublicProductPageUrl(window.location.href)
      || isWebinarsVisualFixtureUrl(window.location.href)
      || isConversationVisualFixtureUrl(window.location.href)
      || isProductCardAnalyticsVisualFixtureUrl(window.location.href)
      || isFrontendSettingsVisualFixtureUrl(window.location.href)
      || isChatbotSettingsVisualFixtureUrl(window.location.href)
      || isFunctionGeneralSettingsVisualFixtureUrl(window.location.href)
      || isFunctionWorkflowBindingSelectorVisualFixtureUrl(window.location.href)
      || isChatbotVisualFixtureUrl(window.location.href)
      || isWidgetPreviewFixtureUrl(window.location.href)
      || isListingsVisualFixtureUrl(window.location.href)
      || isProductFeedVisualFixtureUrl(window.location.href)
      || isInterfaceLoadVisualFixtureUrl(window.location.href)
      || isUserManagementVisualFixtureUrl(window.location.href)
      || isConnectWorkflowVisualFixtureUrl(window.location.href)
      || isConnectWorkflowCanvasVisualFixtureUrl(window.location.href)
      || isTranslationsVisualFixtureUrl(window.location.href);
  }

  shouldSuppressShellLoadFailure(): boolean {
    if (typeof window === 'undefined' || !window.location) {
      return false;
    }

  return isListingsVisualFixtureUrl(window.location.href)
      || isPublicProductPageUrl(window.location.href)
      || isWebinarsVisualFixtureUrl(window.location.href)
      || isConnectWorkflowVisualFixtureUrl(window.location.href)
      || isConnectWorkflowCanvasVisualFixtureUrl(window.location.href)
      || isTranslationsVisualFixtureUrl(window.location.href);
  }

  ngOnDestroy(): void {
    this.cancelBootstrapRetry();
    this.destroyed = true;
    this.authoritativeAccountFingerprint = null;
    this.pendingAccountFingerprint = null;
    this.pendingAccountGeneration = null;
    this.failedAccountFingerprint = null;
    this.accountGeneration++;
    this._destroy$.next(undefined);
    this._destroy$.complete();
  }
}
